/**
 * Personalisation variables: `{{company}}` or `{{company|your team}}` in tooltips, captions,
 * survey text and the end-of-demo button. Values come from the link's query string
 * (`?company=Acme&first_name=Sam`) and, once a viewer fills in the lead form, from that form.
 */
export type Vars = Record<string, string>;

const TOKEN = /\{\{\s*([a-zA-Z][a-zA-Z0-9_]{0,39})\s*(?:\|([^}]{0,120}))?\}\}/g;
/** Query params that are ours, not personalisation. */
const RESERVED = new Set(["src"]);
const MAX_VARS = 20;
const MAX_LEN = 120;

export const normalizeKey = (k: string) => k.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");

/** Replace tokens in plain text. Unknown variables fall back to their default, else to "". */
export function personalize(text: string, vars: Vars): string {
  if (!text || !text.includes("{{")) return text;
  return text.replace(TOKEN, (_, key: string, fallback?: string) => {
    const v = vars[normalizeKey(key)];
    return v && v.trim() ? v : (fallback ?? "").trim();
  }).replace(/ {2,}/g, " ");
}

/** Same, for URLs: substituted values are percent-encoded so they can't change the URL's structure. */
export function personalizeUrl(url: string, vars: Vars): string {
  if (!url || !url.includes("{{")) return url;
  return url.replace(TOKEN, (_, key: string, fallback?: string) => {
    const v = vars[normalizeKey(key)];
    return encodeURIComponent(v && v.trim() ? v : (fallback ?? "").trim());
  });
}

/** Variables from a query string, skipping reserved params and capping count and length. */
export function varsFromSearch(search: URLSearchParams): Vars {
  const out: Vars = {};
  for (const [k, v] of search) {
    const key = normalizeKey(k);
    if (!key || RESERVED.has(key) || Object.keys(out).length >= MAX_VARS) continue;
    const val = v.trim().slice(0, MAX_LEN);
    if (val) out[key] = val;
  }
  return out;
}

/** Variables a lead form contributes: name, first_name, email, company. */
export function varsFromLead(l: { name?: string; email?: string; company?: string }): Vars {
  const out: Vars = {};
  const name = l.name?.trim();
  if (name) { out.name = name; out.first_name = name.split(/\s+/)[0]; }
  if (l.email?.trim()) out.email = l.email.trim();
  if (l.company?.trim()) out.company = l.company.trim();
  return out;
}

/** Variable names used in a piece of text, and whether each use has a fallback. */
export function tokensIn(text: string): { key: string; hasFallback: boolean }[] {
  return [...(text ?? "").matchAll(TOKEN)].map((m) => ({ key: normalizeKey(m[1]), hasFallback: m[2] !== undefined }));
}
